import React, { type ComponentType } from 'react';

import StatusDiscoveryIcon from '@atlaskit/icon/core/status-discovery';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import InfoIcon from '@atlaskit/icon/core/status-information';
import SuccessIcon from '@atlaskit/icon/core/status-success';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { token } from '@atlaskit/tokens';

import type { Appearance, SectionMessageProps } from '../types';

interface AppearanceIconSchema {
	backgroundColor: string;
	primaryIconColor: string;
	Icon: ComponentType<any>;
	LegacyFallbackIcon?: ComponentType<any>;
}

const appearanceIconSchema: {
	[key in Appearance]: AppearanceIconSchema;
} = {
	information: {
		backgroundColor: token('color.background.information'),
		Icon: InfoIcon,
		primaryIconColor: token('color.icon.information'),
	},
	warning: {
		backgroundColor: token('color.background.warning'),
		Icon: WarningIcon,
		primaryIconColor: token('color.icon.warning'),
	},
	error: {
		backgroundColor: token('color.background.danger'),
		Icon: ErrorIcon,
		primaryIconColor: token('color.icon.danger'),
	},
	success: {
		backgroundColor: token('color.background.success'),
		Icon: SuccessIcon,
		primaryIconColor: token('color.icon.success'),
	},
	discovery: {
		backgroundColor: token('color.background.discovery'),
		Icon: StatusDiscoveryIcon,
		primaryIconColor: token('color.icon.discovery'),
	},
};

export function getAppearanceIconStyles(
	appearance: Appearance,
	icon: SectionMessageProps['icon'],
): {
	Icon:
		| React.ElementType<any, keyof React.JSX.IntrinsicElements>
		| (({ primaryColor }: { primaryColor: string }) => React.JSX.Element);
	backgroundColor: string;
	primaryIconColor: string;
	LegacyFallbackIcon?: ComponentType<any>;
} {
	const appearanceIconStyles = appearanceIconSchema[appearance] || appearanceIconSchema.information;
	const AppearanceIcon = ({ primaryColor }: { primaryColor: string }): React.JSX.Element => (
		<appearanceIconStyles.Icon color={primaryColor} spacing="spacious" label={appearance} />
	);
	const Icon = icon || AppearanceIcon;

	return {
		...appearanceIconStyles,
		Icon,
	};
}
